import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiCheck, FiX, FiClock, FiRefreshCw } from 'react-icons/fi';

const TABS = ['Services', 'Locations', 'Providers', 'Bookings'];

const Appointments = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Services');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reminders, setReminders] = useState([]);

  const [serviceForm, setServiceForm] = useState({ name: '', durationMinutes: 30, price: '', currencyCode: '' });
  const [locationForm, setLocationForm] = useState({ name: '', address: '', isVirtual: false });
  const [providerForm, setProviderForm] = useState({ userId: '', displayName: '' });
  const [bookingForm, setBookingForm] = useState({ serviceId: '', providerId: '', customerId: '', locationId: '', meetingLink: '', startsAt: '', notes: '' });

  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');

  const [managingServicesFor, setManagingServicesFor] = useState(null);
  const [providerServiceIds, setProviderServiceIds] = useState([]);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/appointments/services'),
      apiClient.get('/appointments/locations'),
      apiClient.get('/appointments/providers'),
      apiClient.get('/appointments/bookings'),
      apiClient.get('/organisations/current/users').catch(() => ({ data: { data: [] } })),
      apiClient.get('/invoicing/customers').catch(() => ({ data: { data: [] } })),
      apiClient.get('/appointments/reminders').catch(() => ({ data: { data: [] } })),
    ]).then(([svcRes, locRes, provRes, bookRes, usersRes, custRes, remRes]) => {
      setServices(svcRes.data.data);
      setLocations(locRes.data.data);
      setProviders(provRes.data.data);
      setBookings(bookRes.data.data);
      setOrgUsers(usersRes.data.data);
      setCustomers(custRes.data.data);
      setReminders(remRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load appointments data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const serviceName = (id) => services.find((s) => s.id === id)?.name || id;
  const providerName = (id) => providers.find((p) => p.id === id)?.displayName || id;
  const locationName = (id) => locations.find((l) => l.id === id)?.name || '—';
  const customerName = (id) => customers.find((c) => c.id === id)?.name || '—';

  // ---- Services ----
  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...serviceForm };
      if (!payload.price) delete payload.price;
      if (!payload.currencyCode) delete payload.currencyCode;
      await apiClient.post('/appointments/services', payload);
      success('✅ Service added');
      setShowForm(false);
      setServiceForm({ name: '', durationMinutes: 30, price: '', currencyCode: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add service')); }
  };

  const handleToggleServiceActive = async (serviceId, currentlyActive) => {
    try {
      await apiClient.patch(`/appointments/services/${serviceId}`, { isActive: !currentlyActive });
      success(currentlyActive ? '✅ Service deactivated' : '✅ Service reactivated');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update service')); }
  };

  // ---- Locations ----
  const handleAddLocation = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/appointments/locations', locationForm);
      success('✅ Location added');
      setShowForm(false);
      setLocationForm({ name: '', address: '', isVirtual: false });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add location')); }
  };

  // ---- Providers ----
  const handleAddProvider = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/appointments/providers', providerForm);
      success('✅ Provider added');
      setShowForm(false);
      setProviderForm({ userId: '', displayName: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add provider')); }
  };

  const handleToggleProviderActive = async (providerId, currentlyActive) => {
    try {
      await apiClient.patch(`/appointments/providers/${providerId}`, { isActive: !currentlyActive });
      success(currentlyActive ? '✅ Provider deactivated' : '✅ Provider reactivated');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update provider')); }
  };

  const startManageServices = (provider) => {
    setManagingServicesFor(provider.id);
    setProviderServiceIds(provider.serviceIds || []);
  };

  const toggleServiceForProvider = (serviceId) => {
    setProviderServiceIds((prev) => prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]);
  };

  const handleSaveProviderServices = async () => {
    try {
      await apiClient.put(`/appointments/providers/${managingServicesFor}/services`, { serviceIds: providerServiceIds });
      success('✅ Provider services updated');
      setManagingServicesFor(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update services')); }
  };

  // ---- Bookings ----
  const handleCreateBooking = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...bookingForm };
      if (!payload.customerId) delete payload.customerId;
      if (!payload.locationId) delete payload.locationId;
      if (!payload.meetingLink) delete payload.meetingLink;
      if (!payload.notes) delete payload.notes;
      await apiClient.post('/appointments/bookings', payload);
      success('✅ Appointment booked');
      setShowForm(false);
      setBookingForm({ serviceId: '', providerId: '', customerId: '', locationId: '', meetingLink: '', startsAt: '', notes: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not book appointment')); }
  };

  const startReschedule = (b) => {
    setRescheduleId(b.id);
    setRescheduleDate(b.startsAt?.slice(0, 16) || '');
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/appointments/bookings/${rescheduleId}/reschedule`, { startsAt: rescheduleDate });
      success('✅ Appointment rescheduled');
      setRescheduleId(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not reschedule')); }
  };

  const handleCancel = async (appointmentId) => {
    const reason = window.prompt('Reason for cancelling (optional):') || '';
    try {
      await apiClient.post(`/appointments/bookings/${appointmentId}/cancel`, { reason: reason || undefined });
      success('✅ Appointment cancelled');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not cancel')); }
  };

  const handleComplete = async (appointmentId) => {
    try {
      await apiClient.post(`/appointments/bookings/${appointmentId}/complete`);
      success('✅ Marked complete');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not mark complete')); }
  };

  const handleNoShow = async (appointmentId) => {
    try {
      await apiClient.post(`/appointments/bookings/${appointmentId}/no-show`);
      success('✅ Marked as no-show');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not mark no-show')); }
  };

  const handleRunRemindersNow = async () => {
    try {
      const { data } = await apiClient.post('/appointments/reminders/run-now');
      success(`✅ Checked ${data.data.due} due reminder(s), sent ${data.data.sent}`);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not run reminders')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Appointments</h1>
        <p>Services, locations, providers, and bookings</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setManagingServicesFor(null); setRescheduleId(null); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Services' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Services</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Service'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddService} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Name *</label><input required value={serviceForm.name} onChange={(e) => setServiceForm(p => ({ ...p, name: e.target.value }))} placeholder="Consultation" /></div>
              <div className="form-field"><label>Duration (minutes)</label><input type="number" min="5" max="1440" value={serviceForm.durationMinutes} onChange={(e) => setServiceForm(p => ({ ...p, durationMinutes: Number(e.target.value) }))} /></div>
              <div className="form-field"><label>Price</label><input type="number" step="0.01" value={serviceForm.price} onChange={(e) => setServiceForm(p => ({ ...p, price: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Duration</th><th>Price</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {services.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No services yet</td></tr>
                ) : services.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td>{s.durationMinutes} min</td>
                    <td>{s.price ? Number(s.price).toFixed(2) : '—'}</td>
                    <td><span className="badge" style={{ background: s.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{s.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleToggleServiceActive(s.id, s.isActive)}>
                        {s.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Locations' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Locations</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Location'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddLocation} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Name *</label><input required value={locationForm.name} onChange={(e) => setLocationForm(p => ({ ...p, name: e.target.value }))} placeholder="Main Office" /></div>
              <div className="form-field full-width"><label>Address</label><input value={locationForm.address} onChange={(e) => setLocationForm(p => ({ ...p, address: e.target.value }))} /></div>
              <div className="form-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none' }}>
                  <input type="checkbox" checked={locationForm.isVirtual} onChange={(e) => setLocationForm(p => ({ ...p, isVirtual: e.target.checked }))} style={{ width: 'auto' }} />
                  Virtual location
                </label>
              </div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Address</th><th>Type</th></tr></thead>
              <tbody>
                {locations.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No locations yet</td></tr>
                ) : locations.map((l) => (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 600 }}>{l.name}</td>
                    <td>{l.address || '—'}</td>
                    <td>{l.isVirtual ? 'Virtual' : 'Physical'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Providers' && (
        <>
          <section className="page-section">
            <div className="section-header">
              <h2>Providers</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Provider'}
              </button>
            </div>
            {showForm && (
              <form onSubmit={handleAddProvider} className="form-grid" style={{ marginBottom: '1.5rem' }}>
                <div className="form-field">
                  <label>User *</label>
                  <select required value={providerForm.userId} onChange={(e) => setProviderForm(p => ({ ...p, userId: e.target.value }))}>
                    <option value="">-- select --</option>
                    {orgUsers.map((u) => <option key={u.userId} value={u.userId}>{u.firstName} {u.lastName} ({u.email})</option>)}
                  </select>
                </div>
                <div className="form-field"><label>Display Name *</label><input required value={providerForm.displayName} onChange={(e) => setProviderForm(p => ({ ...p, displayName: e.target.value }))} placeholder="Dr. Smith" /></div>
                <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Display Name</th><th>Active</th><th>Actions</th></tr></thead>
                <tbody>
                  {providers.length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No providers yet</td></tr>
                  ) : providers.map((p) => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.displayName}</td>
                      <td><span className="badge" style={{ background: p.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{p.isActive ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <div className="actions">
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => startManageServices(p)}>Manage Services</button>
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleToggleProviderActive(p.id, p.isActive)}>
                            {p.isActive ? 'Deactivate' : 'Reactivate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {managingServicesFor && (
            <section className="page-section">
              <div className="section-header">
                <h2>Services — {providerName(managingServicesFor)}</h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={handleSaveProviderServices}>Save</button>
                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setManagingServicesFor(null)}>Cancel</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {services.map((s) => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', textTransform: 'none', fontWeight: 400 }}>
                    <input type="checkbox" checked={providerServiceIds.includes(s.id)} onChange={() => toggleServiceForProvider(s.id)} style={{ width: 'auto' }} />
                    {s.name} ({s.durationMinutes} min)
                  </label>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {tab === 'Bookings' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Bookings</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Booking'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleCreateBooking} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field">
                <label>Service *</label>
                <select required value={bookingForm.serviceId} onChange={(e) => setBookingForm(p => ({ ...p, serviceId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {services.filter(s => s.isActive).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.durationMinutes} min)</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Provider *</label>
                <select required value={bookingForm.providerId} onChange={(e) => setBookingForm(p => ({ ...p, providerId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {providers.filter(p => p.isActive).map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Customer</label>
                <select value={bookingForm.customerId} onChange={(e) => setBookingForm(p => ({ ...p, customerId: e.target.value }))}>
                  <option value="">-- none --</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Location</label>
                <select value={bookingForm.locationId} onChange={(e) => setBookingForm(p => ({ ...p, locationId: e.target.value }))}>
                  <option value="">-- none --</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Starts At *</label><input required type="datetime-local" value={bookingForm.startsAt} onChange={(e) => setBookingForm(p => ({ ...p, startsAt: e.target.value }))} /></div>
              <div className="form-field"><label>Meeting Link</label><input value={bookingForm.meetingLink} onChange={(e) => setBookingForm(p => ({ ...p, meetingLink: e.target.value }))} /></div>
              <div className="form-field full-width"><label>Notes</label><input value={bookingForm.notes} onChange={(e) => setBookingForm(p => ({ ...p, notes: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Book</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>When</th><th>Service</th><th>Provider</th><th>Customer</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : bookings.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No bookings yet</td></tr>
                ) : bookings.map((b) => (
                  <tr key={b.id}>
                    <td>{new Date(b.startsAt).toLocaleString()}</td>
                    <td>{serviceName(b.serviceId)}</td>
                    <td>{providerName(b.providerId)}</td>
                    <td>{b.customerId ? customerName(b.customerId) : '—'}</td>
                    <td>
                      <span className="badge" style={{
                        background: b.status === 'completed' ? '#10b981' : b.status === 'cancelled' || b.status === 'no_show' ? '#ef4444' : '#f59e0b',
                        color: 'white',
                      }}>{b.status?.replace('_', ' ')}</span>
                    </td>
                    <td>
                      {(b.status === 'confirmed' || b.status === 'rescheduled') && (
                        <div className="actions">
                          <button className="action-icon edit" title="Reschedule" onClick={() => startReschedule(b)}>
                            <FiRefreshCw size={16} />
                          </button>
                          <button className="action-icon edit" title="Complete" onClick={() => handleComplete(b.id)}>
                            <FiCheck size={16} />
                          </button>
                          <button className="action-icon delete" title="No-show" onClick={() => handleNoShow(b.id)}>
                            <FiClock size={16} />
                          </button>
                          <button className="action-icon delete" title="Cancel" onClick={() => handleCancel(b.id)}>
                            <FiX size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rescheduleId && (
            <form onSubmit={handleReschedule} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginTop: '1rem' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>New Date/Time</label>
                <input required type="datetime-local" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Confirm Reschedule</button>
              <button type="button" className="btn btn-outline" style={{ width: 'auto' }} onClick={() => setRescheduleId(null)}>Cancel</button>
            </form>
          )}

          <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ fontSize: '0.95rem', margin: 0 }}>Reminders (24h before each booking, sent automatically every 15 min)</h3>
              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handleRunRemindersNow}>
                Send Due Now
              </button>
            </div>
            {reminders.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>No reminders scheduled yet.</p>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>Send At</th><th>Channel</th><th>Status</th></tr></thead>
                  <tbody>
                    {reminders.map((r) => (
                      <tr key={r.id}>
                        <td>{new Date(r.sendAt).toLocaleString()}</td>
                        <td style={{ textTransform: 'capitalize' }}>{r.channel}</td>
                        <td><span className="badge" style={{ background: r.status === 'sent' ? '#10b981' : r.status === 'failed' ? '#ef4444' : r.status === 'cancelled' ? '#9ca3af' : '#f59e0b', color: 'white' }}>{r.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default Appointments;